import { db } from '@/lib/db';
import type { GroupRoundEnriched, Member, PostMetadataGame, PostMetadataRecapGame } from '@/lib/db/types';
import { applyPointsAwards, processPointsAfterRound } from '@/lib/points/processor';
import { callShotSettleMessage } from '@/lib/shots/call-shot-settle';
import { gameResultsFromSettlement } from '@/lib/games/game-results';
import { roundHasMoneyStakes } from '@/lib/games/round-recap';
import { appliesMoney, stakeFromDbRow } from '@/lib/games/engine/stakes-helpers';
import type { GameStakeConfig } from '@/lib/games/engine/types';
import { grossFromGroupRoundScores, validateRoundGross } from '@/lib/scoring/round-gross';

function stakeDisplayAmount(config: GameStakeConfig | null): number {
  if (!config || !appliesMoney(config)) return 0;
  if (config.moneyPerPoint != null) return config.moneyPerPoint;
  const m = config.money;
  if (typeof m === 'number') return m;
  if (m && typeof m === 'object') {
    return m.perHole ?? m.perLeg ?? m.total ?? m.front ?? m.perPlayer ?? 0;
  }
  return 0;
}

function isCasualRound(round: GroupRoundEnriched): boolean {
  if (round.config?.game_mode === 'casual') return true;
  const enabled = (round.games || []).filter((g) => g.enabled);
  return enabled.length === 0;
}

export async function publishGroupRound(opts: {
  round: GroupRoundEnriched;
  member: Member;
  courseName: string;
  media_urls?: string[];
  body?: string;
  onCallSettled?: (message: string) => void;
}): Promise<string> {
  const { round, member, courseName, media_urls, body, onCallSettled } = opts;
  const poster = round.players?.find((p) => p.member_id === member.id);
  if (!poster) throw new Error('Only players in this round can post the recap');

  const scores = round.scores || [];
  const posterHoleRows = scores.filter((s) => s.player_key === poster.player_key);
  const { gross, hole_scores } = grossFromGroupRoundScores(posterHoleRows, round.holes);
  validateRoundGross({
    gross,
    holes: round.holes,
    holesScored: hole_scores.length,
  });
  const taggedIds = (round.players || [])
    .filter((p) => p.member_id && p.member_id !== member.id)
    .map((p) => p.member_id as string);

  const legacyRound = await db.rounds.create({
    course_id: round.course_id,
    tee_id: round.tee_id || undefined,
    holes: round.holes,
    gross_total: gross,
    played_at: round.played_at,
    notes: `Group round at ${courseName}`,
    hole_scores,
  });

  if (taggedIds.length) await db.roundTags.set(legacyRound.id, taggedIds);

  const gameResults = gameResultsFromSettlement(round.settlement?.game_results);
  const hasMoney = roundHasMoneyStakes(round);

  const recapGames: PostMetadataRecapGame[] = gameResults.map((g) => ({
    type: g.formatId,
    format_id: g.formatId,
    label: g.label,
    summary: g.summary,
    winners: g.winners,
    standings: g.standings?.map((s) => ({
      playerKey: s.playerKey,
      name: s.name,
      value: s.value,
      rank: s.rank,
    })),
  }));

  const settlementGames: PostMetadataGame[] | undefined = hasMoney
    ? gameResults.map((g) => {
        const stake = round.games?.find((row) => row.format_id === g.formatId);
        const config = stake ? stakeFromDbRow(g.formatId, (stake.stakes || {}) as Record<string, unknown>) : null;
        return {
          type: g.formatId,
          format_id: g.formatId,
          scoring: 'net' as const,
          amount: stakeDisplayAmount(config),
          settlement: {
            ...g,
            net: g.netOwed,
          },
        };
      })
    : undefined;

  const groupScores = (round.players || []).map((p) => {
    const holeRows = scores
      .filter((s) => s.player_key === p.player_key && s.strokes != null)
      .map((s) => ({ hole_num: s.hole_num, strokes: s.strokes as number }));
    return {
      id: p.member_id || p.player_key,
      name: p.display_name,
      guest: !p.member_id,
      scores: holeRows,
    };
  });

  const courses = await db.courses.list();
  const courseSlug = courses.find((c) => c.id === round.course_id)?.slug;

  const gameCount = gameResults.length;
  const smack = isCasualRound(round)
    ? `Round at ${courseName} — scorecard only.`
    : `Group round at ${courseName} — ${gameCount} game${gameCount === 1 ? '' : 's'}.`;

  const post = await db.posts.create({
    type: 'round',
    round_id: legacyRound.id,
    body: body ?? smack,
    media_urls,
    metadata: {
      recapGames,
      hasMoneyStakes: hasMoney,
      groupRoundId: round.id,
      ...(settlementGames?.length ? { games: settlementGames } : {}),
      ...(hasMoney && round.settlement ? { settlement: round.settlement as unknown as Record<string, unknown> } : {}),
      tagged: taggedIds,
      courseName,
      ...(courseSlug ? { courseSlug } : {}),
      groupScores,
    },
  });
  const verifyPost = await db.posts.get(post.id);
  if (!verifyPost) {
    throw new Error('Recap was created but is not visible in feed yet. Please retry.');
  }

  await db.groupRounds.setLegacyLinks(round.id, legacyRound.id, post.id);

  const holes = await db.courses.holes(round.course_id);
  const awards = processPointsAfterRound({
    round,
    gameResults,
    holes: [...holes].sort((a, b) => a.hole_num - b.hole_num),
  });
  if (awards.length) {
    await applyPointsAwards(awards);
  }

  try {
    const settle = await db.callShots.tryAutoSettleAfterRoundPost({
      course_id: round.course_id,
      gross_total: gross,
      holes: round.holes,
      played_at: round.played_at,
    });
    if (settle.settled && settle.targetScore != null && settle.actualScore != null && settle.points != null) {
      onCallSettled?.(callShotSettleMessage(settle.targetScore, settle.actualScore, settle.points));
    }
  } catch {
    // Call settle should not block recap post
  }

  return post.id;
}
